import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { adminApi, setAdminAccessToken, AdminUser } from './adminApi';

// Permission helpers — co-located with the auth context so any admin
// component can do `const { canDo } = useAdminAuth()` without an extra
// import. Mirrors backend/src/core/lib/permissions.js exactly.
type Action = 'view' | 'edit';
const RANK = { none: 0, view: 1, edit: 2 } as const;

type AdminAuthCtx = {
  admin: AdminUser | null;
  loading: boolean;
  /**
   * Sign in. `expectedRole` is the role the calling URL declares it
   * accepts — `/admin/login` passes 'admin', `/agent/login` passes
   * 'agent'. Backend rejects mismatches with a generic 401 (no leak
   * of whether the URL was wrong vs the password).
   */
  login: (identifier: string, password: string, expectedRole?: 'admin' | 'agent') => Promise<AdminUser>;
  logout: () => Promise<void>;
  /** Re-fetch /me. Use after a permissions edit so the UI re-evaluates gates. */
  refresh: () => Promise<void>;
  /** Permission helper. Returns true if admin can perform `action` on `key`. */
  canDo: (key: string, action?: Action) => boolean;
};

const Ctx = createContext<AdminAuthCtx | null>(null);

function makeCanDo(admin: AdminUser | null) {
  return (key: string, action: Action = 'view') => {
    if (!admin) return false;
    // Admin role implicitly has 'edit' on everything.
    if (admin.role === 'admin') return true;
    const have = admin.permissions?.[key] ?? 'none';
    return RANK[have] >= RANK[action];
  };
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const ok = await adminApi.tryRefresh();
      if (ok) {
        try {
          const { admin: me } = await adminApi.get<{ admin: AdminUser }>('/admin/auth/me');
          setAdmin(me);
        } catch { setAdmin(null); }
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (identifier: string, password: string, expectedRole?: 'admin' | 'agent') => {
    const body: Record<string, unknown> = { identifier, password };
    if (expectedRole) body.expected_role = expectedRole;
    const r = await adminApi.post<{ accessToken: string; admin: AdminUser }>(
      '/admin/auth/login', body
    );
    setAdminAccessToken(r.accessToken);
    // Refetch /me — login response is enough but /me is the canonical
    // shape and includes last_login_at etc. that the response omits.
    const { admin: me } = await adminApi.get<{ admin: AdminUser }>('/admin/auth/me');
    setAdmin(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try { await adminApi.post('/admin/auth/logout'); } catch { /* noop */ }
    setAdminAccessToken(null);
    setAdmin(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { admin: me } = await adminApi.get<{ admin: AdminUser }>('/admin/auth/me');
      setAdmin(me);
    } catch { /* keep current */ }
  }, []);

  const canDo = makeCanDo(admin);

  return <Ctx.Provider value={{ admin, loading, login, logout, refresh, canDo }}>{children}</Ctx.Provider>;
}

export function useAdminAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>');
  return v;
}

/**
 * Read-only hook returning a permission-check function. Same as
 * `useAdminAuth().canDo` but the named import scans cleaner in JSX.
 *
 *   const can = useCan();
 *   {can('rewards_offers', 'edit') && <button>Approve</button>}
 *
 * For a one-shot check, prefer the named hook over reading from
 * useAdminAuth() — reads better at the call site.
 */
export function useCan() {
  return useAdminAuth().canDo;
}
